import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { ShoppingCart, User, Menu, Sprout, LogOut, Languages, Package } from "lucide-react";
import LoginDialog from "@/components/LoginDialog";

const Header = () => {
  const { t, lang, toggleLang, cartCount, farmer, setFarmer } = useApp();
  const [loginOpen, setLoginOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (path) => location.pathname === path;

  const navLink = (to, label, testId) => (
    <Link
      to={to}
      data-testid={testId}
      className={`px-3 py-2 text-sm font-medium rounded-full transition-colors ${
        isActive(to) ? "bg-[#2D5A27] text-white" : "text-slate-700 hover:bg-[#EFEBE2]"
      }`}
      onClick={() => setMobileOpen(false)}
    >
      {label}
    </Link>
  );

  const handleLogout = () => {
    setFarmer(null);
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-50 bg-[#F9F7F3]/95 backdrop-blur border-b border-[#E2E8F0]" data-testid="site-header">
      <div className="brand-gradient-bar" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 group" data-testid="logo-link">
            <div className="w-10 h-10 rounded-xl bg-[#2D5A27] flex items-center justify-center shadow-sm">
              <Sprout className="w-6 h-6 text-white" strokeWidth={2.2} />
            </div>
            <div className="hidden sm:flex flex-col leading-tight">
              <span className="font-heading font-bold text-[#1E293B] text-base">{t("appName")}</span>
              <span className="text-[10px] uppercase tracking-widest text-[#D97706] font-semibold">Since 1998</span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navLink("/", t("home"), "nav-home")}
            {navLink("/products", t("products"), "nav-products")}
            {farmer && navLink("/orders", t("myOrders"), "nav-orders")}
          </nav>

          {/* Right controls */}
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleLang}
              data-testid="lang-toggle"
              className="rounded-full h-10 px-3 gap-1.5 hover:bg-[#EFEBE2]"
            >
              <Languages className="w-4 h-4" />
              <span className="text-sm font-semibold">{lang === "hi" ? "EN" : "हिं"}</span>
            </Button>

            <Link to="/cart" className="relative" data-testid="cart-link">
              <Button variant="ghost" size="sm" className="rounded-full h-10 w-10 p-0 hover:bg-[#EFEBE2]">
                <ShoppingCart className="w-5 h-5" />
              </Button>
              {cartCount > 0 && (
                <span
                  className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#D97706] text-white text-[10px] font-bold flex items-center justify-center"
                  data-testid="cart-count-badge"
                >
                  {cartCount}
                </span>
              )}
            </Link>

            {farmer ? (
              <div className="hidden md:flex items-center gap-2">
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#EFEBE2]">
                  <User className="w-4 h-4 text-[#2D5A27]" />
                  <span className="text-sm font-medium text-[#1E293B] max-w-[120px] truncate" data-testid="farmer-name-display">
                    {farmer.name}
                  </span>
                </div>
                <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn" className="rounded-full">
                  <LogOut className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <Button
                onClick={() => setLoginOpen(true)}
                data-testid="header-login-btn"
                className="hidden md:inline-flex rounded-full bg-[#2D5A27] hover:bg-[#21421D] text-white h-10 px-5"
              >
                <User className="w-4 h-4 mr-1.5" />
                {t("login")}
              </Button>
            )}

            {/* Mobile menu */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="sm" className="md:hidden rounded-full h-10 w-10 p-0" data-testid="mobile-menu-btn">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-[#F9F7F3] w-[280px]">
                <SheetHeader>
                  <SheetTitle className="text-left font-heading">{t("appName")}</SheetTitle>
                </SheetHeader>
                <div className="flex flex-col gap-2 mt-6">
                  {navLink("/", t("home"), "mobile-nav-home")}
                  {navLink("/products", t("products"), "mobile-nav-products")}
                  {farmer && navLink("/orders", t("myOrders"), "mobile-nav-orders")}
                  <div className="h-px bg-[#E2E8F0] my-2" />
                  {farmer ? (
                    <>
                      <div className="px-3 py-2 rounded-lg bg-[#EFEBE2]">
                        <p className="text-xs text-slate-600">{t("customer")}</p>
                        <p className="font-medium text-sm text-[#1E293B]">{farmer.name}</p>
                        <p className="text-xs text-slate-500">{farmer.mobile}</p>
                      </div>
                      <Button variant="outline" size="sm" onClick={handleLogout} data-testid="mobile-logout-btn" className="rounded-full mt-2">
                        <LogOut className="w-4 h-4 mr-2" /> {t("logout")}
                      </Button>
                    </>
                  ) : (
                    <Button
                      onClick={() => { setLoginOpen(true); setMobileOpen(false); }}
                      data-testid="mobile-login-btn"
                      className="rounded-full bg-[#2D5A27] hover:bg-[#21421D] text-white"
                    >
                      <User className="w-4 h-4 mr-2" /> {t("login")}
                    </Button>
                  )}
                  <Link
                    to="/admin/login"
                    className="text-xs text-slate-500 hover:text-[#2D5A27] mt-4 px-3 flex items-center gap-1.5"
                    onClick={() => setMobileOpen(false)}
                    data-testid="mobile-admin-link"
                  >
                    <Package className="w-3 h-3" /> {t("admin")}
                  </Link>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
      <LoginDialog open={loginOpen} onOpenChange={setLoginOpen} />
    </header>
  );
};

export default Header;
